import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  IconButton,
  Button,
  Divider,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { 
  useGetEvaluationCriteriaQuery,
  useUpdateEvaluationCriteriaMutation 
} from "../../../redux/evaluationCriteriaSlice";
  import { showToast } from '../../../utils/toast';


const EvaluationCriteria = () => {
  const { data: apiWeights, isLoading, isError, error, refetch } = useGetEvaluationCriteriaQuery();
  const [updateWeights, { isLoading: isUpdating }] = useUpdateEvaluationCriteriaMutation();
  const [weights, setWeights] = useState({});
  
  // Update local state when API data is loaded
  useEffect(() => {
    refetch()
    if (apiWeights) {
      setWeights(apiWeights?.prompt);
    }
  }, [apiWeights]);

  const [newKey, setNewKey] = useState("");
  const [newValue, setNewValue] = useState("");

  const handleWeightChange = (key, value) => {
    setWeights((prev) => ({
      ...prev,
      [key]: Number(value),
    }));
  };

  const handleRemove = (key) => {
    const updated = { ...weights };
    delete updated[key];
    setWeights(updated);
  };

  const handleAdd = () => {
    if (!newKey || !newValue) return;

    setWeights((prev) => ({
      ...prev,
      [newKey.trim().toLowerCase()]: Number(newValue),
    }));

    setNewKey("");
    setNewValue("");
  };


  const handleSave = async () => {
    try {
      await updateWeights({
        id: apiWeights.id, // or get the correct ID from your API response
        prompt: weights
      }).unwrap();
      showToast('success', 'Weights saved successfully');
      refetch(); // Refresh the data after successful update
    } catch (err) {
      console.error('Failed to save weights:', err);
      showToast('error', 'Failed to save weights. Please try again.');
    }
  };

  const handleUpdateWeight = async (key, value) => {
    try {
      const updatedWeights = { ...weights, [key]: Number(value) };
      await updateWeights({
        id: apiWeights.id,
        prompt: updatedWeights
      }).unwrap();
      showToast('success', 'Weight updated successfully');
      refetch();
    } catch (err) {
      console.error('Failed to update weight:', err);
      showToast('error', 'Failed to update weight. Please try again.');
    }
  };

  const totalWeight = Object.values(weights).reduce(
    (sum, val) => sum + val,
    0
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box maxWidth={700} mx="auto" mt={4}>
        <Alert severity="error">
          Error loading evaluation criteria: {error?.data?.message || 'Unknown error'}
        </Alert>
      </Box>
    );
  }


const globalUser = JSON.parse(localStorage.getItem("globalUser"));
  const isTrial = globalUser?.account?.is_trial;

  return (
    <Box maxWidth={700} mx="auto" mt={4} style={{ paddingTop: `${isTrial ? "30px" : "0px"}` }}>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h5">
              Candidate Evaluation Criteria
            </Typography>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleSave}
              disabled={isUpdating}
              startIcon={isUpdating ? <CircularProgress size={20} /> : null}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>

          <Stack spacing={2}>
            {Object.entries(weights).map(([key, value]) => (
              <Stack
                key={key}
                direction="row"
                spacing={2}
                alignItems="center"
              >
                <TextField
                  label="Section"
                  value={key}
                  fullWidth
                  disabled
                />

                <TextField
                  label="Weight"
                  type="number"
                  value={value}
                  onChange={(e) => handleWeightChange(key, e.target.value)}
                  onBlur={(e) => handleUpdateWeight(key, e.target.value)}
                  sx={{ width: 120 }}
                />

                <IconButton
                  color="error"
                  onClick={() => handleRemove(key)}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle1" gutterBottom>
            Add New Section
          </Typography>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Section key"
              placeholder="eg. certifications"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              fullWidth
            />

            <TextField
              label="Weight"
              type="number"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              sx={{ width: 140 }}
            />

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add
            </Button>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6">
            Total Weight: {totalWeight}
          </Typography>

          {totalWeight !== 100 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Total weight should be exactly 100
            </Alert>
          )}

          {/* <Divider sx={{ my: 3 }} />

          <Typography variant="subtitle2">
            Payload Preview (send to backend)
          </Typography>

          <Box
            component="pre"
            sx={{
              bgcolor: "#f5f5f5",
              p: 2,
              borderRadius: 1,
              fontSize: 13,
              overflow: "auto",
            }}
          >
            {JSON.stringify(weights, null, 2)}
          </Box> */}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EvaluationCriteria;
